import { createReducer, on } from '@ngrx/store';
import { PartData } from '@shared/gui/models/part-data.model';
import { ClosedPath, GraphConfig, GraphTolerance } from '../models/graph.model';
import { PathElement } from '../models/path-element.model';
import { PathGasBlock } from '../models/path-gas-block.model';

import * as GasBlockActions from './gas-block.actions';
import { ExportConfig } from '../models/chart-export.model';
import { OriginPathGasBlock } from '../models/origin-gas-block.model';

export const gasBlockFeatureKey = 'gasBlock';

export enum GraphPanel {
  None,
  Reduced,
  Choice,
  Normal,
  Expanded,
  Order
}

export interface State {
  display: GraphPanel;
  orderedParts: Array<PartData>;
  pathKeys: Array<string>;
  pathPoints: Array<PartData>;
  closedPaths: Array<ClosedPath>;
  pathElements: Array<PathElement>;
  pathGasBlocks: Array<PathGasBlock>;
  graphConfig: GraphConfig;
  tolerances: Array<GraphTolerance>;
  blockTolerance: GraphTolerance;
  exportConfig: ExportConfig;
  isExporting: boolean;
  displayOnlyPath: boolean;
  displayOnlyPathOrigin: boolean;
  lastOriginGasblock: OriginPathGasBlock;
}

const initialState: State = {
  display: GraphPanel.None,
  orderedParts: [],
  pathKeys: [],
  pathPoints: [],
  closedPaths: [],
  pathElements: [],
  pathGasBlocks: [],
  graphConfig: {
    isBlock: false,
    showThresholds: false,
    data: [],
    colors: ['#f8ff33', '#b3ffff']
  },
  tolerances: [
    {
      min: null,
      max: null,
      name: null
    }
  ],
  blockTolerance: null,
  exportConfig: null,
  isExporting: false,
  displayOnlyPath: false,
  lastOriginGasblock: null,
  displayOnlyPathOrigin: false
};

export const reducer = createReducer(
  initialState,
  on(GasBlockActions.resetStateApply, state => ({
    ...initialState,
    graphConfig: state.graphConfig,
    tolerances: state.tolerances,
    exportConfig: state.exportConfig,
    isExporting: state.isExporting
  })),
  on(GasBlockActions.init, (state, { path, elements, gasBlocks }) => ({
    ...state,
    display: state.display === GraphPanel.None ? GraphPanel.Reduced : state.display,
    pathKeys: path,
    blockedPaths: [],
    pathElements: elements,
    pathGasBlocks: gasBlocks,
    closedPaths: []
  })),
  on(GasBlockActions.update, (state, { elements, gasBlocks, closedPaths }) => ({
    ...state,
    pathElements: elements,
    pathGasBlocks: gasBlocks,
    closedPaths: closedPaths
  })),
  on(GasBlockActions.orderParts, (state, { parts }) => ({
    ...state,
    orderedParts: parts
  })),
  on(GasBlockActions.savePathPoints, (state, { parts }) => ({
    ...state,
    pathPoints: parts
  })),
  on(GasBlockActions.selectDisplay, (state, { display }) => ({
    ...state,
    display: display
  })),
  on(GasBlockActions.selectConfig, (state, { config }) => ({
    ...state,
    graphConfig: config
  })),
  on(GasBlockActions.setTolerances, (state, { tolerances }) => ({
    ...state,
    tolerances
  })),
  on(GasBlockActions.setBlockTolerance, (state, { tolerance }) => ({
    ...state,
    blockTolerance: tolerance
  })),
  on(GasBlockActions.initExport, (state, { config }) => ({
    ...state,
    exportConfig: { ...config, pathKeys: state.pathKeys },
    isExporting: true
  })),
  on(GasBlockActions.endExport, state => ({
    ...state,
    exportConfig: null,
    isExporting: false
  })),
  on(GasBlockActions.displayFavoritePath, (state, { favoritePath }) => ({
    ...state,
    graphConfig: {
      ...state.graphConfig,
      data: favoritePath.properties,
      isBlock: favoritePath.isGasBlock
    }
  })),
  on(GasBlockActions.displayOnlyPath, (state, { displayOnlyPath }) => ({
    ...state,
    displayOnlyPath
  })),
  on(GasBlockActions.getLastOriginGasBlockSuccess, (state, { lastOriginGasblock }) => ({
    ...state,
    lastOriginGasblock
  })),
  on(GasBlockActions.setDisplayOnlyPathOrigin, (state, { displayOnlyPathOrigin }) => ({
    ...state,
    displayOnlyPathOrigin
  }))
);
